/**
 * Enterprise Authentication Service
 * Handles all authentication operations with the backend
 */

import apiService, { refreshAccessToken } from './apiClient';
import secureStorage from '../utils/secureStorage';
import logger from '../utils/logger';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface LoginCredentials {
  username: string;
  password: string;
  organizationId: number;
  device_info: DeviceInfo;
  otp?: string; // Optional for 2FA
}

export interface FieldUserLoginCredentials {
  mobile: string;
  organizationId: number;
  device_info: DeviceInfo;
  otp?: string; // Optional - sent on second call after OTP is received
}

export interface CustomerLoginCredentials {
  identifier: string; // mobile or email
  password?: string; // For password-based auth
  otp?: string; // For OTP-based auth
  organizationId: number;
  device_info: DeviceInfo;
}

export interface DeviceInfo {
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  screenResolution?: string;
}

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  name: string;
  mobile: string;
  userCode: string;
  designation: string;
  userRoleId: string;
  userRoleName: string;
  loginTypeId: number;
  loginTypeName: string;
  organizationId: number;
  orgId: number;
  organization: {
    id: number;
    name: string;
    logo?: string;
    status: string;
    timezone?: string;
    dateFormat?: string;
    timeFormat?: string;
    currency?: string;
    customerAssignType?: 'direct' | 'territory';
  };
  status: string;
  twoFactorEnabled: boolean;
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  expiresAt: string;
  dataRights?: 'ALL' | 'OWN' | 'TEAM' | 'DEPARTMENT';
  reportingManagerId?: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    sessionId: string;
    expiresAt: string;
  };
}

export interface OTPRequiredResponse {
  success: boolean;
  message: string;
  data: {
    message: string;
    mobile: string;
  };
}

export interface LookupUserResponse {
  success: boolean;
  message: string;
  data: {
    organizationId: number;
    org_name: string;
    logo?: string;
    banner_image?: string;
    favicon?: string;
    login_screen_images?: string[];
    branding_colors?: Record<string, string>;
    welcome_message?: string;
    requires_2fa: boolean;
    user_type: 'corporate_user' | 'sales_user' | 'customer';
  };
}

export interface RefreshTokenResponse {
  success: boolean;
  data: {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
  };
}

// ============================================================================
// AUTHENTICATION SERVICE
// ============================================================================

class AuthService {
  private readonly BASE_PATH = '/auth/web';

  /**
   * Get device information for device fingerprinting
   */
  getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;

    // Detect browser
    let browser = 'Unknown';
    let browserVersion = 'Unknown';

    if (userAgent.includes('Chrome')) {
      browser = 'Chrome';
      const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Firefox')) {
      browser = 'Firefox';
      const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browser = 'Safari';
      const match = userAgent.match(/Version\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    } else if (userAgent.includes('Edge')) {
      browser = 'Edge';
      const match = userAgent.match(/Edge\/(\d+\.\d+)/);
      if (match) browserVersion = match[1];
    }

    // Detect OS
    let os = 'Unknown';
    let osVersion = 'Unknown';

    if (platform.includes('Win')) {
      os = 'Windows';
      if (userAgent.includes('Windows NT 10.0')) osVersion = '10';
      else if (userAgent.includes('Windows NT 11.0')) osVersion = '11';
    } else if (platform.includes('Mac')) {
      os = 'macOS';
    } else if (platform.includes('Linux')) {
      os = 'Linux';
    } else if (userAgent.includes('Android')) {
      os = 'Android';
    } else if (userAgent.includes('iOS')) {
      os = 'iOS';
    }

    // Detect device type
    let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = 'mobile';
    } else if (/iPad|Tablet/i.test(userAgent)) {
      deviceType = 'tablet';
    }

    return {
      browser,
      browserVersion,
      os,
      osVersion,
      deviceType,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };
  }

  /**
   * Corporate Login - Username/Password with optional 2FA
   */
  async corporateLogin(credentials: Omit<LoginCredentials, 'device_info'>): Promise<LoginResponse | OTPRequiredResponse> {
    try {
      const payload: LoginCredentials = {
        ...credentials,
        device_info: this.getDeviceInfo(),
      };

      logger.info('Corporate login - payload being sent to API', {
        username: payload.username,
        organizationId: payload.organizationId,
        hasPassword: !!payload.password,
        device_info: payload.device_info
      });

      const response = await apiService.post<any>(
        `${this.BASE_PATH}/corporate-login`,
        payload
      );

      // Response is unwrapped by interceptor - check for user directly
      if (response.user) {
        this.saveAuthData(response);
        logger.info('Corporate login successful', { userId: response.user.id });
      }

      return response;
    } catch (error: any) {
      logger.error('Corporate login failed', error);
      throw error;
    }
  }

  /**
   * Field User Login - Mobile + OTP (works like corporate login with 2FA)
   */
  async fieldUserLogin(credentials: Omit<FieldUserLoginCredentials, 'device_info'>): Promise<LoginResponse | OTPRequiredResponse> {
    try {
      const payload: FieldUserLoginCredentials = {
        ...credentials,
        device_info: this.getDeviceInfo(),
      };

      logger.info('Field user login - payload being sent to API', {
        mobile: payload.mobile,
        organizationId: payload.organizationId,
        hasOtp: !!payload.otp,
        device_info: payload.device_info
      });

      const response = await apiService.post<any>(
        `${this.BASE_PATH}/field-user-login`,
        payload
      );

      // Response is unwrapped by interceptor - check for user directly
      if (response.user) {
        this.saveAuthData(response);
        logger.info('Field user login successful', { userId: response.user.id });
      }

      return response;
    } catch (error: any) {
      logger.error('Field user login failed', error);
      throw error;
    }
  }

  /**
   * Customer Login - Password-based authentication
   * For dealers, distributors, retailers, and other customers
   */
  async customerLogin(credentials: Omit<CustomerLoginCredentials, 'device_info'>): Promise<LoginResponse | OTPRequiredResponse> {
    try {
      const payload: CustomerLoginCredentials = {
        ...credentials,
        device_info: this.getDeviceInfo(),
      };

      logger.info('Customer login - payload being sent to API', {
        identifier: payload.identifier,
        organizationId: payload.organizationId,
        hasPassword: !!payload.password,
        hasOtp: !!payload.otp,
        device_info: payload.device_info
      });

      const response = await apiService.post<any>(
        `${this.BASE_PATH}/customer-login`,
        payload
      );

      // Response is unwrapped by interceptor - check for user directly
      if (response.user) {
        this.saveAuthData(response);
        logger.info('Customer login successful', { userId: response.user.id });
      }

      return response;
    } catch (error: any) {
      logger.error('Customer login failed', error);
      throw error;
    }
  }

  /**
   * Customer Login with OTP - OTP-based authentication
   * For dealers, distributors, retailers, and other customers
   */
  async customerLoginWithOTP(credentials: Omit<CustomerLoginCredentials, 'device_info' | 'password'>): Promise<LoginResponse | OTPRequiredResponse> {
    try {
      const payload: Omit<CustomerLoginCredentials, 'password'> = {
        ...credentials,
        device_info: this.getDeviceInfo(),
      };

      logger.info('Customer OTP login - payload being sent to API', {
        identifier: payload.identifier,
        organizationId: payload.organizationId,
        hasOtp: !!payload.otp,
        device_info: payload.device_info
      });

      const response = await apiService.post<any>(
        `${this.BASE_PATH}/customer-login`,
        payload
      );

      // Response is unwrapped by interceptor - check for user directly
      if (response.user) {
        this.saveAuthData(response);
        logger.info('Customer OTP login successful', { userId: response.user.id });
      }

      return response;
    } catch (error: any) {
      logger.error('Customer OTP login failed', error);
      throw error;
    }
  }

  /**
   * Verify OTP and complete login (2FA)
   */
  async verifyOTP(userId: string, otpCode: string): Promise<LoginResponse> {
    try {
      const response = await apiService.post<any>(
        `${this.BASE_PATH}/verify-otp`,
        { userId, otpCode }
      );

      // API returns statusCode instead of success
      if ((response.statusCode === 200 || response.success) && response.data) {
        this.saveAuthData(response.data);
        logger.info('OTP verification successful - auth data saved', { userId });
      }

      return response;
    } catch (error: any) {
      logger.error('OTP verification failed', error);
      throw error;
    }
  }

  /**
   * Resend OTP
   */
  async resendOTP(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiService.post(
        `${this.BASE_PATH}/resend-otp-generic`,
        { userId }
      );

      logger.info('OTP resent successfully', { userId });
      return response;
    } catch (error: any) {
      logger.error('Resend OTP failed', error);
      throw error;
    }
  }

  /**
   * Lookup user by identifier (username/email/mobile)
   * Step 1 of login flow - discovers organization
   */
  async lookupUser(identifier: string): Promise<LookupUserResponse> {
    try {
      const response = await apiService.post<LookupUserResponse>(
        `${this.BASE_PATH}/lookup-user`,
        { identifier }
      );

      logger.info('User lookup successful', { identifier });
      return response;
    } catch (error: any) {
      logger.error('User lookup failed', error);
      throw error;
    }
  }

  /**
   * Get organization branding
   */
  async getOrganizationBranding(organizationId?: number, subdomain?: string): Promise<any> {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (organizationId) params.append('organizationId', organizationId.toString());
      if (subdomain) params.append('subdomain', subdomain);

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await apiService.get(
        `${this.BASE_PATH}/organization-branding${queryString}`
      );

      logger.info('Organization branding fetched');
      return response;
    } catch (error: any) {
      logger.error('Get organization branding failed', error);
      throw error;
    }
  }

  /**
   * Refresh access token.
   *
   * Delegates to apiClient's `refreshAccessToken()` — the same mutexed
   * implementation the reactive 401-interceptor and the proactive refresh
   * scheduler already use — instead of running a second, separate call
   * against the same endpoint. The backend rotates the refresh token on
   * every use and revokes the whole token family on reuse (replay-attack
   * protection), so two independent "refresh" code paths racing on the
   * same not-yet-rotated token doesn't just waste a request — it can
   * revoke a session that had just refreshed fine a moment earlier. There
   * must be exactly one refresh mutex in this app; apiClient's is it.
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      await refreshAccessToken();

      // apiClient's refreshAccessToken() already wrote the new tokens to
      // secureStorage — read them back to keep this method's return shape
      // for its other caller (store/auth/login/thunk.ts).
      const authUser = secureStorage.getAuthUser();
      return {
        success: true,
        data: {
          accessToken: secureStorage.getAccessToken() ?? '',
          refreshToken: secureStorage.getRefreshToken() ?? '',
          expiresAt: authUser?.expiresAt ?? '',
        },
      };
    } catch (error: any) {
      logger.error('Token refresh failed', error);
      throw error;
    }
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      await apiService.post(`${this.BASE_PATH}/logout`);

      this.clearAuthData();
      logger.info('Logout successful');
    } catch (error: any) {
      // Even if API call fails, clear local data
      this.clearAuthData();
      logger.error('Logout error (local data cleared)', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const accessToken = secureStorage.getAccessToken();
    const authUser = secureStorage.getAuthUser();

    if (!accessToken || !authUser) {
      return false;
    }

    // Check token expiration
    try {
      const expiresAt = new Date(authUser.expiresAt);
      const now = new Date();

      // Add 5-minute buffer
      const bufferMs = 5 * 60 * 1000;

      if (expiresAt.getTime() - now.getTime() < bufferMs) {
        // Token expired or expiring soon
        logger.warn('Token expired or expiring soon');
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Error checking token expiration', error);
      return false;
    }
  }

  /**
   * Get current authenticated user
   */
  getCurrentUser(): AuthUser | null {
    return secureStorage.getAuthUser();
  }

  /**
   * Update stored user data in secure storage (for profile updates)
   */
  updateStoredUser(userData: AuthUser): void {
    secureStorage.setAuthUser(userData);
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    return secureStorage.getAccessToken();
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return secureStorage.getRefreshToken();
  }

  /**
   * Save authentication data to secure storage
   */
  private saveAuthData(data: LoginResponse['data']): void {
    secureStorage.setAccessToken(data.accessToken);
    secureStorage.setRefreshToken(data.refreshToken);

    // Merge expiresAt and sessionId from response data into user object
    const userWithExpiry = {
      ...data.user,
      expiresAt: data.expiresAt,
      sessionId: data.sessionId,
      accessToken: data.accessToken,
      refreshToken: data.refreshToken
    };

    secureStorage.setAuthUser(userWithExpiry);

    logger.debug('Auth data saved to secure storage', {
      userId: data.user.id,
      expiresAt: data.expiresAt
    });
  }

  /**
   * Clear authentication data
   */
  private clearAuthData(): void {
    secureStorage.clearAuthData();
    logger.debug('Auth data cleared from secure storage');
  }
}

// Create and export singleton instance
const authService = new AuthService();
export default authService;